import { useEffect, useRef } from "react";
import {
  FieldValues,
  Path,
  useFormContext,
  UseFormRegisterReturn,
} from "react-hook-form";

/* ================================
 * Types
 * ================================ */
type VisibleMap = Record<string, unknown> | null | undefined;

export type UseCleanWhenHiddenOptions = {
  // map-mode
  nullifyHidden?: boolean; // default true
  unregisterHidden?: boolean; // default false
  shouldDirty?: boolean; // default true
  ignore?: string[]; // names to skip

  // specs-mode
  ready?: boolean; // start only when true (default true)
  mode?: "null" | "undefined"; // what to write when hiding (default "null")
  keepRegistered?: boolean; // keep fields registered (default true)
};

export type Spec<T extends FieldValues = any> = {
  name: Path<T>;
  isVisible: boolean;
  ignore?: boolean;
};

/* ================================
 * Small helpers
 * ================================ */

// Normalize options with defaults
function normalizeOptions(opts: UseCleanWhenHiddenOptions = {}) {
  return {
    nullifyHidden: opts.nullifyHidden ?? true,
    unregisterHidden: opts.unregisterHidden ?? false,
    shouldDirty: opts.shouldDirty ?? true,
    ignoreSet: new Set(opts.ignore ?? []),
    ready: opts.ready ?? true,
    mode: opts.mode ?? "null",
    keepRegistered: opts.keepRegistered ?? true,
  };
}

// Map-mode: current visible set from visibleMap
function buildVisibleSetFromMap(visibleMap: VisibleMap) {
  return new Set<string>(visibleMap ? Object.keys(visibleMap) : []);
}

// Specs-mode: prime previous visibility snapshot (do not clean yet)
function primePrevMapFromSpecs<T extends FieldValues>(
  specs: ReadonlyArray<Spec<T>>,
) {
  const next: Record<string, boolean> = {};
  specs.forEach((s) => (next[s.name as string] = !!s.isVisible));
  return next;
}

// Apply cleanup for fields that just flipped from visible -> hidden (specs mode)
function applyCleanupForSpecs<T extends FieldValues>(args: {
  specs: ReadonlyArray<Spec<T>>;
  prev: Record<string, boolean>;
  ignoreSet: Set<string>;
  mode: "null" | "undefined";
  shouldDirty: boolean;
  unregisterHidden: boolean;
  setValue: (
    name: Path<T>,
    value: any,
    opts?: { shouldDirty?: boolean; shouldValidate?: boolean },
  ) => void;
  clearErrors: (name: Path<T>) => void;
  unregister: (name: Path<T>) => void;
}) {
  const {
    specs,
    prev,
    ignoreSet,
    mode,
    shouldDirty,
    unregisterHidden,
    setValue,
    clearErrors,
    unregister,
  } = args;

  const next: Record<string, boolean> = {};

  specs.forEach((s) => {
    const key = s.name as string;
    const wasVisible = !!prev[key];
    const nowVisible = !!s.isVisible;
    next[key] = nowVisible;

    if (s.ignore || ignoreSet.has(key)) return;

    if (wasVisible && !nowVisible) {
      setValue(s.name as any, mode === "null" ? null : undefined, {
        shouldDirty,
        shouldValidate: true,
      });
      clearErrors(s.name as any);
      if (unregisterHidden) unregister(s.name as any);
    }
  });

  return next;
}

// Apply cleanup for fields that just flipped from visible -> hidden (map mode)
function applyCleanupForMap(args: {
  currentVisible: Set<string>;
  prev: Record<string, boolean>;
  ignoreSet: Set<string>;
  nullifyHidden: boolean;
  unregisterHidden: boolean;
  shouldDirty: boolean;
  setValue: (
    name: string,
    value: any,
    opts?: { shouldDirty?: boolean },
  ) => void;
  unregister: (name: string) => void;
}) {
  const {
    currentVisible,
    prev,
    ignoreSet,
    nullifyHidden,
    unregisterHidden,
    shouldDirty,
    setValue,
    unregister,
  } = args;

  // compute next snapshot
  const next: Record<string, boolean> = {};
  currentVisible.forEach((n) => (next[n] = true));

  // fields that were visible but are now hidden
  for (const name of Object.keys(prev)) {
    if (prev[name] && !currentVisible.has(name)) {
      if (ignoreSet.has(name)) continue;
      if (nullifyHidden) setValue(name as any, null, { shouldDirty });
      if (unregisterHidden) unregister(name as any);
    }
  }

  return next;
}

// Specs-mode: optionally pre-register fields to avoid RHF drop-offs
function useRegisterSpecs<T extends FieldValues>(
  specs: ReadonlyArray<Spec<T>> | null,
  keepRegistered: boolean,
  register: (name: Path<T>) => UseFormRegisterReturn,
) {
  useEffect(() => {
    if (!specs || !keepRegistered) return;
    specs.forEach((s) => register(s.name));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [specs, keepRegistered, register]);
}

/* ================================
 * Hook overloads
 * ================================ */
export function useCleanWhenHidden(
  visibleMap: VisibleMap,
  opts?: UseCleanWhenHiddenOptions,
): void;

export function useCleanWhenHidden<T extends FieldValues>(
  specs: ReadonlyArray<Spec<T>>,
  opts?: UseCleanWhenHiddenOptions,
): void;

/* ================================
 * Main hook
 * ================================ */
export function useCleanWhenHidden(
  arg1: VisibleMap | ReadonlyArray<Spec<any>>,
  opts?: UseCleanWhenHiddenOptions,
) {
  const { setValue, unregister, register, clearErrors } = useFormContext();
  const isSpecs = Array.isArray(arg1);
  const {
    nullifyHidden,
    unregisterHidden,
    shouldDirty,
    ignoreSet,
    ready,
    mode,
    keepRegistered,
  } = normalizeOptions(opts);

  // specs pre-registration (no-op in map mode)
  useRegisterSpecs(
    isSpecs ? (arg1 as ReadonlyArray<Spec<any>>) : null,
    keepRegistered,
    register,
  );

  // previous visibility snapshot
  const prevRef = useRef<Record<string, boolean>>({});

  useEffect(() => {
    if (isSpecs) {
      const specs = arg1 as ReadonlyArray<Spec<any>>;

      // bootstrap only until ready
      if (!ready) {
        prevRef.current = primePrevMapFromSpecs(specs);
        return;
      }

      prevRef.current = applyCleanupForSpecs({
        specs,
        prev: prevRef.current,
        ignoreSet,
        mode,
        shouldDirty,
        unregisterHidden,
        setValue,
        clearErrors,
        unregister,
      });
      return;
    }

    // map mode
    const visibleMap = arg1 as VisibleMap;
    const currentVisible = buildVisibleSetFromMap(visibleMap);

    prevRef.current = applyCleanupForMap({
      currentVisible,
      prev: prevRef.current,
      ignoreSet,
      nullifyHidden,
      unregisterHidden,
      shouldDirty,
      setValue: setValue as any,
      unregister: unregister as any,
    });
  }, [
    arg1,
    isSpecs,
    ready,
    mode,
    nullifyHidden,
    unregisterHidden,
    shouldDirty,
    ignoreSet,
    setValue,
    unregister,
    clearErrors,
  ]);
}

export function FormCleaner({
  specs,
  ready,
  keepRegistered = true,
}: {
  specs: ReadonlyArray<{ name: any; isVisible: boolean; ignore?: boolean }>;
  ready: boolean;
  keepRegistered?: boolean;
}) {
  useCleanWhenHidden(specs, {
    ready,
    mode: "null",
    keepRegistered: keepRegistered,
    unregisterHidden: !keepRegistered,
    shouldDirty: true,
  });
  return null;
}
